import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { SpeakerHigh, SpeakerLow, SpeakerSlash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { normalizeLang } from "@/lib/i18n";
import { getVolume, installMasterVolume, setVolume, subscribe } from "@/lib/masterVolume";

// The master volume in the top bar, just left of Help (product owner, 2026-10-31).
export const COPY = {
  en: { volume: "Volume", mute: "Mute", unmute: "Unmute" },
  "pt-BR": { volume: "Volume", mute: "Silenciar", unmute: "Ativar som" },
  "es-419": { volume: "Volumen", mute: "Silenciar", unmute: "Activar sonido" },
  "fr-CA": { volume: "Volume", mute: "Couper le son", unmute: "Rétablir le son" },
};

// The screens that play audio and carry the shared header.
const AUDIO_SCREENS = [/^\/upload$/, /^\/prepare\/[^/]+$/, /^\/interview\/[^/]+$/, /^\/live\/[^/]+$/, /^\/report\/[^/]+$/];

export function VolumeSlider({ lang = "en" }) {
  const [vol, setVol] = useState(getVolume());
  const [open, setOpen] = useState(false); // small screens: the slider opens from the icon
  const wrapRef = useRef(null);
  const c = COPY[lang] || COPY.en;
  useEffect(() => subscribe(setVol), []);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  const silent = vol.muted || vol.level === 0;
  const Icon = silent ? SpeakerSlash : vol.level < 0.5 ? SpeakerLow : SpeakerHigh;
  const percent = Math.round((vol.muted ? 0 : vol.level) * 100);
  const toggleMute = () => setVolume({ muted: !silent, level: silent && vol.level === 0 ? 0.6 : vol.level });
  const slider = (
    <input
      type="range"
      min="0"
      max="100"
      step="5"
      value={percent}
      onChange={(e) => setVolume({ level: Number(e.target.value) / 100 })}
      aria-label={c.volume}
      aria-valuetext={`${percent}%`}
      className="w-24 accent-[color:var(--brand)] cursor-pointer"
      data-testid="master-volume-slider"
    />
  );

  return (
    <div ref={wrapRef} className="relative" data-testid="master-volume">
      <div className="flex h-10 items-center gap-2 rounded-full border border-[color:var(--border-stone)] bg-white px-3 shadow-sm">
        <button
          type="button"
          onClick={() => (window.matchMedia?.("(min-width: 640px)").matches ? toggleMute() : setOpen((o) => !o))}
          aria-label={silent ? c.unmute : c.mute}
          title={silent ? c.unmute : c.mute}
          className="text-[color:var(--brand)]"
          data-testid="master-volume-button"
        >
          <Icon size={20} weight="duotone" />
        </button>
        <div className="hidden sm:flex items-center">{slider}</div>
      </div>
      {open && (
        <div className="sm:hidden absolute right-0 top-12 z-50 flex items-center gap-3 rounded-xl border border-[color:var(--border-stone)] bg-white p-3 shadow-lg">
          <button type="button" onClick={toggleMute} aria-label={silent ? c.unmute : c.mute} className="text-[color:var(--brand)]">
            <Icon size={20} weight="duotone" />
          </button>
          {slider}
        </div>
      )}
    </div>
  );
}

/** Places the volume control just left of the header's Help button on audio screens. */
export default function GlobalVolumeControl() {
  const { user } = useAuth();
  const location = useLocation();
  const [target, setTarget] = useState(null);
  const lang = normalizeLang(user?.preferred_language);

  useEffect(() => { installMasterVolume(); }, []);

  useEffect(() => {
    setTarget(null);
    if (!user || typeof document === "undefined") return undefined;
    if (!AUDIO_SCREENS.some((re) => re.test(location.pathname))) return undefined;

    const host = document.createElement("div");
    host.setAttribute("data-testid", "master-volume-host");
    host.className = "shrink-0";

    const place = () => {
      const help = document.querySelector('header [data-testid="help-button"], [data-testid="app-header"] [data-testid="help-button"]');
      if (!help) return;
      const anchor = help.closest('[data-testid="global-help-host"]') || help;
      const row = anchor.parentElement;
      if (!row) return;
      if (host.parentElement !== row || host.nextSibling !== anchor) row.insertBefore(host, anchor);
      setTarget((t) => (t === host ? t : host));
    };

    place();
    const observer = new MutationObserver(place);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      host.remove();
      setTarget(null);
    };
  }, [user, location.pathname]);

  return target ? createPortal(<VolumeSlider lang={lang} />, target) : null;
}
